import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import WorkspaceKpis from '../components/workspace/WorkspaceKpis';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import { useSettings } from '../store/SettingsContext';
import ProgrammeService from '../services/ProgrammeService';
import { formatDate } from '../utils/dateFormat';

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return null;
  return date;
};

const buildDayMatch = (dayLabel, todayLabel) =>
  dayLabel?.toLowerCase().includes(todayLabel.toLowerCase());

export default function TeacherDashboardPage() {
  const navigate = useNavigate();
  const { students, incidents } = useStudentStore();
  const { user } = useUser();
  const { settings } = useSettings();
  const programmeService = useMemo(() => new ProgrammeService(), []);
  const programmes = useMemo(() => programmeService.list(), [programmeService]);

  const attendanceThreshold = settings.attendanceThreshold || 70;
  const dayLabel = new Date().toLocaleDateString('en-GB', { weekday: 'long' });

  const flaggedStudents = useMemo(
    () =>
      students.filter((student) => {
        const attendanceRate = student.attendance?.rate || 0;
        const hasOpenSafeguarding = student.safeguardingEvents?.some((event) => event.status === 'open');
        return (
          ['high', 'critical'].includes(student.riskLevel) ||
          attendanceRate < attendanceThreshold ||
          hasOpenSafeguarding ||
          student.status === 'Under Review'
        );
      }),
    [students, attendanceThreshold]
  );

  const upcomingReviews = useMemo(() => {
    const today = new Date();
    const nextFortnight = new Date(today);
    nextFortnight.setDate(today.getDate() + 14);
    const rows = [];
    students.forEach((student) => {
      (student.supportReviews || []).forEach((review) => {
        const due = parseDateValue(review.dueDate || review.reviewDate);
        if (due && due <= nextFortnight && due >= today) {
          rows.push({ type: 'Support review', due, studentName: student.name, id: review.id });
        }
      });
      (student.safeguardingEvents || []).forEach((event) => {
        const due = parseDateValue(event.reviewDate);
        if (due && due <= nextFortnight && due >= today) {
          rows.push({ type: 'Safeguarding review', due, studentName: student.name, id: event.id });
        }
      });
    });
    return rows.sort((a, b) => a.due - b.due).slice(0, 6);
  }, [students]);

  const overdueFollowUps = useMemo(() => {
    const today = new Date();
    return incidents.filter((incident) => {
      if (!incident.followUpRequired || !incident.followUpDate) return false;
      if (incident.status === 'closed') return false;
      const due = parseDateValue(incident.followUpDate);
      return due && due < today;
    });
  }, [incidents]);

  const todayProgrammes = useMemo(() => {
    return programmes.filter((programme) => {
      if (!programme.day) return false;
      if (user?.accessScope === 'staff') {
        return programme.staff?.toLowerCase() === (user?.name || '').toLowerCase() && buildDayMatch(programme.day, dayLabel);
      }
      return buildDayMatch(programme.day, dayLabel);
    });
  }, [programmes, user, dayLabel]);

  const upcomingProgrammes = useMemo(() => {
    return programmes
      .filter((programme) => {
        if (user?.accessScope === 'staff') {
          return programme.staff?.toLowerCase() === (user?.name || '').toLowerCase();
        }
        return true;
      })
      .slice(0, 5);
  }, [programmes, user]);

  const attendanceAlerts = students.filter((student) => (student.attendance?.rate || 0) < attendanceThreshold).length;
  const openSafeguarding = students.reduce(
    (sum, student) => sum + (student.safeguardingEvents || []).filter((event) => event.status === 'open').length,
    0
  );

  const kpis = [
    { id: 'students', label: 'Students in scope', value: students.length },
    { id: 'flags', label: 'Student flags', value: flaggedStudents.length, badge: { label: 'Review', variant: 'danger' } },
    { id: 'attendance', label: `Below ${attendanceThreshold}%`, value: attendanceAlerts },
    { id: 'safeguarding', label: 'Open safeguarding', value: openSafeguarding }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Teacher Dashboard</h2>
          <p className="sp-muted">Your students, lessons, and actions due.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => navigate('/students')}>
            View students
          </Button>
          <Button size="sm" onClick={() => navigate('/attendance')}>
            Mark attendance
          </Button>
        </div>
      </div>

      <WorkspaceKpis items={kpis} />

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Student flags</h3>
            <Badge variant="danger">{flaggedStudents.length}</Badge>
          </div>
          {flaggedStudents.length === 0 ? (
            <p className="text-sm sp-muted">No flagged students right now.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {flaggedStudents.slice(0, 5).map((student) => (
                <button
                  key={student.id}
                  type="button"
                  className="sp-button sp-button--ghost w-full text-left"
                  onClick={() => navigate(`/students/${student.id}/dashboard`)}
                >
                  {student.name} · {student.riskLevel}
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Overdue follow-ups</h3>
            <Badge variant={overdueFollowUps.length ? 'danger' : 'inactive'}>{overdueFollowUps.length}</Badge>
          </div>
          {overdueFollowUps.length === 0 ? (
            <p className="text-sm sp-muted">No overdue follow-ups.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {overdueFollowUps.slice(0, 5).map((incident) => (
                <button
                  key={incident.id}
                  type="button"
                  className="sp-button sp-button--ghost w-full text-left"
                  onClick={() => navigate(`/incidents/${incident.id}`)}
                >
                  {incident.type} · due {formatDate(incident.followUpDate)}
                </button>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Upcoming reviews</h3>
            <Badge variant={upcomingReviews.length ? 'review' : 'inactive'}>{upcomingReviews.length}</Badge>
          </div>
          {upcomingReviews.length === 0 ? (
            <p className="text-sm sp-muted">No reviews due in the next 14 days.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {upcomingReviews.map((review) => (
                <div key={`${review.type}-${review.id}`}>
                  <div className="sp-value" style={{ fontWeight: 600 }}>{review.studentName}</div>
                  <div className="text-xs sp-muted">{review.type} · due {formatDate(review.due)}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-5 space-y-3">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Today&apos;s lessons</h3>
          {todayProgrammes.length === 0 ? (
            <p className="text-sm sp-muted">No sessions scheduled for today.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {todayProgrammes.map((programme) => (
                <div key={programme.id} className="sp-panel sp-panel--muted p-3">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{programme.name}</div>
                  <div className="text-xs sp-muted">{programme.day} · {programme.time} · {programme.location || 'Location TBC'}</div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5 space-y-3">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Upcoming programmes</h3>
          {upcomingProgrammes.length === 0 ? (
            <p className="text-sm sp-muted">No programmes assigned yet.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {upcomingProgrammes.map((programme) => (
                <div key={programme.id} className="sp-panel sp-panel--muted p-3">
                  <div className="sp-value" style={{ fontWeight: 600 }}>{programme.name}</div>
                  <div className="text-xs sp-muted">{programme.day} · {programme.time} · Staff: {programme.staff || 'TBC'}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
